import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import shortid from 'shortid';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import Legend from '../Legend';

import './StackedBar.css';
import {
  AXIS_TEXT_COLOR,
  TOOLTIP_TEXT_COLOR,
  // eslint-disable-next-line import/namespace
} from '../../src/ReportsTabs/reports-constants';

// Utility function to convert camelCase to Title Case
const camelCaseToTitleCase = (str) => {
  return str
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (match) => match.toUpperCase()) // Capitalize first letter
    .trim(); // Remove any leading/trailing spaces
};

const formatPercentage = (value) => {
  return typeof value === 'number' ? value.toFixed(2) : value;
};

// Transform stacked bar data to Recharts format
const transformDataForRecharts = (stackedBars, xLabels) => {
  return stackedBars.map((bar, index) => {
    const total = bar.reduce((acc, segment) => acc + segment.value, 0);
    const dataPoint = {
      name: xLabels.bottom[index],
      topLabel: xLabels.top[index],
      total: total,
    };

    // Add each segment as a property
    bar.forEach((segment, segmentIndex) => {
      let segmentValue = segment.value;

      // Convert to percentage for display
      if (total > 0) {
        segmentValue = (segment.value / total) * 100;
      }

      dataPoint[`segment${segmentIndex}`] = segmentValue;
      dataPoint[`segment${segmentIndex}_color`] = segment.color;
      dataPoint[`segment${segmentIndex}_zone`] = segment.zone;
      dataPoint[`segment${segmentIndex}_originalValue`] = segment.value;
    });

    return dataPoint;
  });
};

// Get unique segments across all bars for creating Bar components
const getSegmentKeys = (stackedBars) => {
  const maxSegments = Math.max(...stackedBars.map((bar) => bar.length));
  return Array.from({ length: maxSegments }, (_, i) => `segment${i}`);
};

// Custom tooltip for accessibility
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        className="recharts-tooltip"
        style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
        role="tooltip"
        aria-live="polite"
        aria-label={`Data for ${data.topLabel}: ${
          data.name
        }. Total: ${formatPercentage(data.total)}. ${payload
          .map((entry) => {
            const segmentIndex = entry.dataKey.replace('segment', '');
            const zone = data[`segment${segmentIndex}_zone`];
            const percentageValue = data[`segment${segmentIndex}`];
            const titleCaseZone = camelCaseToTitleCase(zone || '');
            return `${titleCaseZone}: ${formatPercentage(percentageValue)}`;
          })
          .join('. ')}`}
      >
        <p
          style={{ color: TOOLTIP_TEXT_COLOR }}
        >{`${data.topLabel}: ${data.name}`}</p>
        <p style={{ color: TOOLTIP_TEXT_COLOR }}>{`Total: ${formatPercentage(
          data.total
        )}`}</p>
        {payload.reverse().map((entry, index) => {
          const segmentIndex = entry.dataKey.replace('segment', '');
          const zone = data[`segment${segmentIndex}_zone`];
          const percentageValue = data[`segment${segmentIndex}`];
          const titleCaseZone = camelCaseToTitleCase(zone || '');
          return (
            <p key={index} style={{ color: TOOLTIP_TEXT_COLOR }}>
              {`${titleCaseZone}: ${formatPercentage(percentageValue)}`}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const calculateTicks = (n = 1) => {
  const size = n < 1 ? 100 : 100 / n;
  return Array.from({ length: n < 1 ? 1 : n }, (_, i) => {
    const tempValue = Math.ceil(size * (i + 1));
    if (tempValue < 100)
      return { value: tempValue, text: `${tempValue}%`, type: 'major' };
    return { value: 100, text: `${100}%`, type: 'major' };
  });
};

const CHART_TOP_MARGIN = 62;
const TOP_LABEL_MAX_LINES = 3;
const TOP_LABEL_LINE_HEIGHT = 1.2;
const TOP_LABEL_MAX_FONT_SIZE = 14;
// Horizontal breathing room so adjacent titles never touch edge to edge
const TOP_LABEL_GUTTER = 12;

// Custom X-axis tick component that shows both top and bottom labels
const CustomXAxisTick = ({ x, y, payload, xLabels, width }) => {
  const barCount = xLabels.bottom.length;
  const columnWidth = width ? width / barCount : 100;
  const labelWidth = Math.max(20, columnWidth - TOP_LABEL_GUTTER);
  const topFontSize =
    columnWidth < 40 ? 11 : columnWidth < 60 ? 12 : TOP_LABEL_MAX_FONT_SIZE;
  const bottomFontSize = columnWidth < 40 ? 10 : columnWidth < 60 ? 11 : 13;
  const topLabel = xLabels.top[payload.index];
  const topLabelHeight = Math.ceil(
    topFontSize * TOP_LABEL_LINE_HEIGHT * TOP_LABEL_MAX_LINES
  );

  return (
    <g>
      <foreignObject
        x={x - labelWidth / 2}
        y={6}
        width={labelWidth}
        height={topLabelHeight}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className="stacked-bar-axis-top-label"
          style={{ fontSize: topFontSize }}
          title={topLabel}
        >
          {topLabel}
        </div>
      </foreignObject>
      <text
        x={x}
        y={y + 12}
        textAnchor="middle"
        fill={AXIS_TEXT_COLOR}
        fontSize={bottomFontSize}
        fontFamily="Source Sans Pro, sans-serif"
      >
        {payload.value}
      </text>
    </g>
  );
};

const StackedBar = ({
  data = {
    description: '',
    percentage: {
      active: false,
      numberOfTicks: 20,
    },
    yLabels: {
      type: 'minor',
    },
  },
  height = 360,
  id,
  minWidth = 600,
  className = '',
  chartWidth,
  ...otherProps
}) => {
  const {
    description,
    xLabels,
    yLabels,
    stackedBars,
    percentage,
    legend,
  } = data;

  // Keyboard navigation state
  const [focusedBarIndex, setFocusedBarIndex] = useState(-1);
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [showKeyboardTooltip, setShowKeyboardTooltip] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const chartContainerRef = useRef(null);

  const maxTotalBars = stackedBars.map((bar) => {
    return bar.reduce((acc, val) => {
      return acc + val.value;
    }, 0);
  });

  // Transform data for Recharts
  const chartData = transformDataForRecharts(stackedBars, xLabels, percentage);
  const segmentKeys = getSegmentKeys(stackedBars);

  // Keyboard navigation functions
  const announceBarData = useCallback(
    (barIndex) => {
      if (barIndex >= 0 && barIndex < chartData.length) {
        const dataPoint = chartData[barIndex];
        const segments = segmentKeys
          .map((segmentKey) => {
            const percentageValue = dataPoint[segmentKey];
            const zone = dataPoint[`${segmentKey}_zone`];
            if (percentageValue !== undefined) {
              const titleCaseZone = camelCaseToTitleCase(zone || '');
              return `${titleCaseZone}: ${formatPercentage(percentageValue)}`;
            }
            return null;
          })
          .filter(Boolean)
          .join(', ');

        const announcement = `Bar ${barIndex + 1} of ${chartData.length}. ${
          dataPoint.topLabel
        }: ${dataPoint.name}. Total: ${formatPercentage(
          dataPoint.total
        )}. Segments: ${segments}`;
        setAnnouncement(announcement);
      }
    },
    [chartData, segmentKeys]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!isKeyboardMode) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setFocusedBarIndex((prevIndex) => {
            const newIndex =
              prevIndex > 0 ? prevIndex - 1 : chartData.length - 1;
            announceBarData(newIndex);
            return newIndex;
          });
          break;

        case 'ArrowRight':
          event.preventDefault();
          setFocusedBarIndex((prevIndex) => {
            const newIndex =
              prevIndex < chartData.length - 1 ? prevIndex + 1 : 0;
            announceBarData(newIndex);
            return newIndex;
          });
          break;

        case 'Home':
          event.preventDefault();
          setFocusedBarIndex(0);
          announceBarData(0);
          break;

        case 'End':
          event.preventDefault();
          // eslint-disable-next-line no-case-declarations
          const lastIndex = chartData.length - 1;
          setFocusedBarIndex(lastIndex);
          announceBarData(lastIndex);
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (focusedBarIndex >= 0) {
            setShowKeyboardTooltip((prev) => !prev);
            setAnnouncement(
              showKeyboardTooltip ? 'Tooltip hidden' : 'Tooltip shown'
            );
          }
          break;

        case 'Escape':
          event.preventDefault();
          if (showKeyboardTooltip) {
            setShowKeyboardTooltip(false);
            setAnnouncement('Tooltip hidden');
          } else {
            setIsKeyboardMode(false);
            setFocusedBarIndex(-1);
            chartContainerRef.current?.blur();
          }
          break;

        default:
          break;
      }
    },
    [
      isKeyboardMode,
      focusedBarIndex,
      chartData.length,
      showKeyboardTooltip,
      announceBarData,
    ]
  );

  const handleFocus = useCallback(() => {
    setIsKeyboardMode(true);
    if (focusedBarIndex === -1) {
      setFocusedBarIndex(0);
      announceBarData(0);
    }
  }, [focusedBarIndex, announceBarData]);

  const handleBlur = useCallback(() => {
    // Small delay to allow for re-focus within component
    setTimeout(() => {
      if (document.activeElement !== chartContainerRef.current) {
        setIsKeyboardMode(false);
        setFocusedBarIndex(-1);
        setShowKeyboardTooltip(false);
      }
    }, 100);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsKeyboardMode(false);
    setFocusedBarIndex(-1);
    setShowKeyboardTooltip(false);
  }, []);

  // Effect to manage keyboard event listeners
  useEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) return;

    chartContainer.addEventListener('keydown', handleKeyDown);
    chartContainer.addEventListener('focus', handleFocus);
    chartContainer.addEventListener('blur', handleBlur);
    chartContainer.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      chartContainer.removeEventListener('keydown', handleKeyDown);
      chartContainer.removeEventListener('focus', handleFocus);
      chartContainer.removeEventListener('blur', handleBlur);
      chartContainer.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [handleKeyDown, handleFocus, handleBlur, handleMouseEnter]);

  const renderLegend = (specs) => {
    return <Legend {...specs} className="styled-legend" />;
  };

  // YLabels must contain the biggest value in the graph
  const maxTick = Math.max.apply(
    null,
    yLabels.map((o) => o.value)
  );
  let maxValue = Math.round(Math.max.apply(null, maxTotalBars));
  Math.max(maxTick, maxValue);
  let yTicks = yLabels.slice();
  if (percentage.active) {
    yTicks = calculateTicks(percentage.numberOfTicks || 1);
  } else if (maxValue > maxTick) {
    yTicks.push({
      value: maxValue,
      text: String(maxValue),
      type: 'major',
    });
  }
  yTicks = yTicks.sort((a, b) => b.value - a.value);

  const styleBarChart = { height: `${height + 1}px` };
  const containerid = id || `barchartLegend-${shortid.generate()}`;
  const chartId = `${containerid}-chart`;
  const descriptionId = `${containerid}-description`;
  const dataTableId = `${containerid}-data-table`;

  const classes = classNames('ng-ui-widgets', 'ng-ui-stacked-bar', className);

  const styleChart = chartWidth ? { width: `${chartWidth}px` } : {};
  const wrapperQueryStyle = {
    ...styleChart,
    minWidth: `${minWidth}px`,
    ...(chartWidth && window.innerWidth <= 320
      ? { '--chart-width': `${chartWidth + 38}px` }
      : {}),
  };

  return (
    <div className={classes} {...otherProps}>
      {description && (
        <div id={descriptionId} className="title">
          {description}
        </div>
      )}
      <div
        className={classNames('wrapper-query', {
          'wrapper-query-with-width': chartWidth && window.innerWidth <= 320,
        })}
        style={{
          ...wrapperQueryStyle,
          overflow: 'visible',
        }}
      >
        <div className="wrapper" style={{ overflow: 'visible' }}>
          <div className="bar-chart" style={styleBarChart}>
            <div
              ref={chartContainerRef}
              id={chartId}
              className={classNames('recharts-container', {
                'keyboard-navigation-mode': isKeyboardMode,
              })}
              style={{
                width: '100%',
                height: height + 40,
                position: 'relative',
              }}
              tabIndex={0}
              role="img"
              aria-label={`${
                description || 'Stacked bar chart'
              }. Use arrow keys to navigate between bars, Enter or Space to show/hide details, Escape to exit navigation.`}
              aria-describedby={
                description ? `${descriptionId} ${dataTableId}` : dataTableId
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: CHART_TOP_MARGIN,
                    right: 30,
                    left: 0,
                    bottom: 50,
                  }}
                  barCategoryGap="20%"
                  maxBarSize={90}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid
                    strokeDasharray="none"
                    stroke="#8C8C8C"
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={
                      // Do not pass `width`: Recharts injects the real
                      // plot-area width into the tick, and a custom `width`
                      // prop would override it (see CartesianAxis#renderTicks).
                      <CustomXAxisTick xLabels={xLabels} height={height} />
                    }
                    axisLine={{ stroke: AXIS_TEXT_COLOR }}
                    tickLine={false}
                    interval={0}
                    height={40}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    axisLine={{ stroke: AXIS_TEXT_COLOR }}
                    tickLine={false}
                    tick={{
                      fontSize: 13,
                      fontFamily: 'Source Sans Pro, sans-serif',
                      fill: AXIS_TEXT_COLOR,
                    }}
                  />
                  <Tooltip
                    content={<CustomTooltip percentage={percentage} />}
                  />

                  {/* Generate Bar components for each segment */}
                  {segmentKeys.map((segmentKey) => {
                    // Get the color for this segment from the first bar that has it
                    const firstBarWithSegment = chartData.find(
                      (d) => d[segmentKey] !== undefined
                    );
                    const segmentColor = firstBarWithSegment
                      ? firstBarWithSegment[`${segmentKey}_color`]
                      : '#cccccc';
                    const segmentZone = firstBarWithSegment
                      ? firstBarWithSegment[`${segmentKey}_zone`]
                      : '';
                    const titleCaseZone = camelCaseToTitleCase(
                      segmentZone || ''
                    );

                    return (
                      <Bar
                        key={segmentKey}
                        dataKey={segmentKey}
                        stackId="stack"
                        fill={segmentColor}
                        strokeWidth={0}
                        aria-label={`${titleCaseZone} segment`}
                        aria-describedby={dataTableId}
                        role="presentation"
                      />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>

              {/* Keyboard focus indicator */}
              {isKeyboardMode && focusedBarIndex >= 0 && (
                <div
                  className="keyboard-focus-indicator"
                  style={{
                    position: 'absolute',
                    left: `${(focusedBarIndex / chartData.length) * 100}%`,
                    width: `${100 / chartData.length}%`,
                    height: '100%',
                    top: 0,
                    border: '3px solid #4285f4',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxSizing: 'border-box',
                  }}
                  aria-hidden="true"
                />
              )}

              {/* Keyboard tooltip */}
              {isKeyboardMode && focusedBarIndex >= 0 && (
                <div
                  className={`keyboard-tooltip ${
                    showKeyboardTooltip ? 'visible' : 'hidden'
                  }`}
                  style={{
                    position: 'absolute',
                    left: `${
                      (focusedBarIndex / chartData.length) * 100 +
                      50 / chartData.length
                    }%`,
                    top: '10px',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1001,
                    maxWidth:
                      Math.min(300, (chartWidth || minWidth) * 0.8) + 'px',
                    minWidth:
                      Math.min(220, (chartWidth || minWidth) * 0.4) + 'px',
                    pointerEvents: 'none',
                    opacity: showKeyboardTooltip ? 1 : 0,
                    visibility: showKeyboardTooltip ? 'visible' : 'hidden',
                    transition:
                      'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
                    willChange: 'opacity, visibility',
                  }}
                  role="tooltip"
                  aria-live="polite"
                >
                  {(() => {
                    const dataPoint = chartData[focusedBarIndex];

                    return (
                      <div>
                        <p
                          style={{ color: TOOLTIP_TEXT_COLOR }}
                        >{`${dataPoint.topLabel}: ${dataPoint.name}`}</p>
                        <p
                          style={{ color: TOOLTIP_TEXT_COLOR }}
                        >{`Total: ${formatPercentage(dataPoint.total)}`}</p>
                        {segmentKeys
                          .map((segmentKey, i) => {
                            const percentageValue = dataPoint[segmentKey];
                            const zone = dataPoint[`${segmentKey}_zone`];
                            if (percentageValue !== undefined) {
                              const titleCaseZone = camelCaseToTitleCase(
                                zone || ''
                              );
                              return (
                                <p
                                  key={i}
                                  style={{ color: TOOLTIP_TEXT_COLOR }}
                                >
                                  {`${titleCaseZone}: ${formatPercentage(
                                    percentageValue
                                  )}`}
                                </p>
                              );
                            }
                            return null;
                          })
                          .filter(Boolean)}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Screen reader accessibility */}
              <div
                id={dataTableId}
                className="screen-reader-text"
                role="table"
                aria-label="Stacked bar chart data table"
                aria-labelledby={description ? descriptionId : undefined}
              >
                <div role="rowgroup">
                  <div role="row">
                    <span role="columnheader">Category</span>
                    <span role="columnheader">Total</span>
                    <span role="columnheader">Segments</span>
                  </div>
                </div>
                <div role="rowgroup">
                  {chartData.map((dataPoint, i) => (
                    <div key={i} role="row" aria-rowindex={i + 2}>
                      <span
                        role="cell"
                        aria-describedby={`${dataTableId}-category-${i}`}
                      >
                        <span id={`${dataTableId}-category-${i}`}>
                          {`${dataPoint.name} (${dataPoint.topLabel})`}
                        </span>
                      </span>
                      <span
                        role="cell"
                        aria-describedby={`${dataTableId}-total-${i}`}
                      >
                        <span id={`${dataTableId}-total-${i}`}>
                          {`Total: ${formatPercentage(dataPoint.total)}`}
                        </span>
                      </span>
                      <span
                        role="cell"
                        aria-describedby={`${dataTableId}-segments-${i}`}
                      >
                        <span id={`${dataTableId}-segments-${i}`}>
                          {segmentKeys
                            .map((segmentKey) => {
                              const percentageValue = dataPoint[segmentKey];
                              const zone = dataPoint[`${segmentKey}_zone`];
                              if (percentageValue !== undefined) {
                                const titleCaseZone = camelCaseToTitleCase(
                                  zone || ''
                                );
                                return `${titleCaseZone}: ${formatPercentage(
                                  percentageValue
                                )}; `;
                              }
                              return '';
                            })
                            .join('')}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ARIA live region for announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="screen-reader-text"
        role="status"
      >
        {announcement}
      </div>

      {legend &&
        renderLegend({
          id: containerid,
          data: legend,
          mode: 'horizontal',
          'aria-describedby': chartId,
        })}
    </div>
  );
};

StackedBar.propTypes = {
  data: PropTypes.shape({
    description: PropTypes.string,
    xLabels: PropTypes.shape({
      top: PropTypes.arrayOf(PropTypes.string).isRequired,
      bottom: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    yLabels: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number,
        text: PropTypes.string,
        type: PropTypes.oneOf(['major', 'minor']),
      })
    ).isRequired,
    legend: PropTypes.array,
    stackedBars: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          value: PropTypes.number,
          zone: PropTypes.string,
        })
      )
    ).isRequired,
    percentage: PropTypes.shape({
      active: PropTypes.bool,
      numberOfTicks: PropTypes.number,
    }),
  }).isRequired,
  height: PropTypes.number,
  minWidth: PropTypes.number,
  chartWidth: PropTypes.number,
  id: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

export default StackedBar;
